import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table/Table';
import TaskForm from '@components/ModalComponents/TaskForm';

import { useQueryPortalAvailableProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalAvailableProjects';
import { useMutatePortalTasks } from '@hooks/portal/tasks/useMutatePortalTasks';
import { useQueryPortalAvailableMilestones } from '@hooks/portal/tasks/useQueryPortalAvailableMilestones';
import { useQueryPortalTask } from '@hooks/portal/tasks/useQueryPortalTask';
import { useQueryPortalTasks } from '@hooks/portal/tasks/useQueryPortalTasks';
import { useQueryPortalTasksAssigneesFilter } from '@hooks/portal/tasks/useQueryPortalTasksAssigneesFilter';
import { useQueryPortalTasksPriorities } from '@hooks/portal/tasks/useQueryPortalTasksPriorities';
import { useQueryPortalTasksStatuses } from '@hooks/portal/tasks/useQueryPortalTasksStatuses';
import { useSearchParamsQuery } from '@hooks/useSearchParamsQuery';

import { portalTasks as portalTasksSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetPortalTasks,
	setAssignees,
	setMilestoneId,
	setPage,
	setPageSize,
	setPriority,
	setSearchValue,
	setSort,
	setStatus,
} from '@slices/portalTasks';

import { setFilterUtil, setMultiSelectFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';

import { TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_480, MODAL_WIDTH_772, modalCloseConfirm } from '@constants/modals';
import { TableFilterTypes } from '@constants/table';
import { portalSearchQueries } from '../constants';
import { deleteConfirmDescription, deleteConfirmText, searchTooltipText } from './constants';

import { getColumns } from './manageTasksColumns';

import styles from './Task.module.scss';

interface ITasksProps {
	projectId: string;
	isProjectActive: boolean;
	isPortalAdmin: boolean;
}

const Tasks = ({
	projectId,
	isProjectActive,
	isPortalAdmin,
}: ITasksProps) => {
	const dispatch = useAppDispatch();
	const query = useSearchParamsQuery();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const queryTaskId = query.get(portalSearchQueries.task);

	const removeSearchParams = () => {
		navigate(pathname, { replace: true });
	};

	const {
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		status,
		priority,
		assignees,
		milestoneId,
	} = useAppSelector(portalTasksSelector);

	const [selectedTaskStatus, setSelectedTaskStatus] = useState<TSelectOption<string>>(null);
	const [selectedTaskPriority, setSelectedTaskPriority] = useState<TSelectOption<string>>(null);
	const [selectedMilestoneId, setSelectedMilestoneId] = useState<TSelectOption<string>>(null);
	const [selectedTaskAssignees, setSelectedTaskAssignees] = useState<TSelectOption<string>[] | null>(null);

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isTaskFormModal, setIsTaskFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isViewMode, setIsViewMode] = useState(false);
	const [editableTaskId, setEditableTaskId] = useState<string | null>(null);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);

	const openDeleteConfirm = () => {
		setIsShowDeleteConfirm(prevState => !prevState);
	};

	const closeDeleteConfirm = () => {
		setIsShowDeleteConfirm(false);
		setSingleIdForRemove(null);
	};

	const openTaskFormModal = () => setIsTaskFormModal(true);

	const closeModals = () => {
		if (queryTaskId) removeSearchParams();

		setIsTaskFormModal(false);
		setIsCloseConfirmModal(false);
		setIsEditMode(false);
		setEditableTaskId(null);
	};

	const closeTaskViewModal = () => {
		if (queryTaskId) removeSearchParams();

		setIsViewMode(false);
		setEditableTaskId(null);
	};

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	useEffect(() => {
		if (!queryTaskId) return;

		setEditableTaskId(queryTaskId);

		if (isProjectActive) {
			setIsEditMode(true);
		} else {
			setIsViewMode(true);
		}
	}, [queryTaskId, isProjectActive]);

	const {
		portalTasks,
		isLoadingPortalTasks,
		countOfRecords,
	} = useQueryPortalTasks(projectId);

	const { portalTask, isFetchingPortalTask } = useQueryPortalTask(editableTaskId);

	const { portalTasksStatuses, isLoadingPortalTasksStatuses } = useQueryPortalTasksStatuses(projectId);
	const { portalTasksPriorities, isLoadingPortalTasksPriorities } = useQueryPortalTasksPriorities();
	const { portalAvailableProjects, isLoadingPortalAvailableProjects } = useQueryPortalAvailableProjects();

	const {
		portalAvailableMilestones,
		isLoadingPortalAvailableMilestones,
	} = useQueryPortalAvailableMilestones(projectId);

	const {
		portalTasksAssigneesFilter,
		isLoadingPortalTasksAssigneesFilter,
	} = useQueryPortalTasksAssigneesFilter(projectId);

	const {
		handleDeleteTask,
		handleDeleteTasks,
		isLoadingDelete,
		createTask,
		updateTask,
	} = useMutatePortalTasks({
		projectId,
		priority,
		status,
		searchValue,
		assignees,
		closeDeleteConfirm,
	});

	const columns = useMemo(() => getColumns({
		setIsEditMode,
		setEditableTaskId,
		openDeleteConfirm,
		setSingleIdForRemove,
		isProjectActive,
		setIsViewMode,
		isPortalAdmin,
	}), [isProjectActive, isPortalAdmin]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedTaskStatus,
				options: portalTasksStatuses,
				minWidth: '200px',
				setState: setSelectedTaskStatus,
				defaultValue: status,
			},
			{
				name: 'priority',
				placeholder: 'Priority',
				option: selectedTaskPriority,
				options: portalTasksPriorities,
				minWidth: '200px',
				setState: setSelectedTaskPriority,
				defaultValue: priority,
			},
			{
				type: TableFilterTypes.MULTISELECT,
				name: 'assignees',
				placeholder: 'Assignee',
				option: selectedTaskAssignees,
				options: portalTasksAssigneesFilter,
				minWidth: '200px',
				setState: setSelectedTaskAssignees,
				defaultValue: assignees,
			},
			{
				name: 'milestoneId',
				placeholder: 'Milestone',
				option: selectedMilestoneId,
				options: portalAvailableMilestones,
				minWidth: '200px',
				setState: setSelectedMilestoneId,
				defaultValue: milestoneId,
			},
		];
	}, [
		portalTasksStatuses,
		selectedTaskStatus,
		status,
		portalTasksPriorities,
		selectedTaskPriority,
		priority,
		assignees,
		portalTasksAssigneesFilter,
		selectedTaskAssignees,
		milestoneId,
		selectedMilestoneId,
		portalAvailableMilestones,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedTaskStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: 'status',
			dispatch,
		});
	}, [dispatch, status, selectedTaskStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedTaskPriority,
			currentValue: priority,
			setAction: setPriority,
			fieldName: 'priority',
			dispatch,
		});
	}, [dispatch, priority, selectedTaskPriority]);

	useEffect(() => {
		setMultiSelectFilterUtil({
			selectedValues: selectedTaskAssignees,
			setAction: setAssignees,
			fieldName: 'assignees',
			dispatch,
		});
	}, [dispatch, selectedTaskAssignees]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedMilestoneId,
			currentValue: milestoneId,
			setAction: setMilestoneId,
			fieldName: 'milestoneId',
			dispatch,
		});
	}, [dispatch, milestoneId, selectedMilestoneId]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, []);

	const deleteItemsHandler = useCallback(() => {
		if (singleIdForRemove) return handleDeleteTask(singleIdForRemove);

		return handleDeleteTasks({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeleteTask,
		handleDeleteTasks,
		singleIdForRemove,
	]);

	const isLoading = isLoadingPortalTasks
		|| isLoadingPortalTasksStatuses
		|| isLoadingPortalTasksPriorities
		|| isLoadingPortalTasksAssigneesFilter
		|| isLoadingPortalAvailableProjects
		|| isLoadingPortalAvailableMilestones
		|| isFetchingPortalTask;

	const defaultProject = (portalAvailableProjects && !isViewMode && !isEditMode
		? portalAvailableProjects.find(project => project?.value === projectId)
		: undefined) as TSelectOption<string>;

	useEffect(() => {
		return () => {
			dispatch(resetPortalTasks());
		};
	}, [dispatch]);

	return (
		<div className={styles.tasksTableContainer}>
			<Modal
				isDirtyForm={isDirtyFormModal}
				isOpen={isTaskFormModal}
				onClose={toggleCloseConfirmModal}
				title='Add task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeModals}
					createTask={createTask}
					defaultProject={defaultProject}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>

			<Modal
				isDirtyForm={isDirtyFormModal}
				isOpen={isEditMode && !isLoading}
				onClose={toggleCloseConfirmModal}
				title='Edit Task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeModals}
					isEditMode={isEditMode}
					portalTask={portalTask}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
					updateTask={updateTask}
				/>
			</Modal>

			<Modal
				isOpen={isViewMode && !isLoading}
				onClose={closeTaskViewModal}
				title='View task'
				width={MODAL_WIDTH_772}
			>
				<TaskForm
					closeModals={closeTaskViewModal}
					isViewMode={isViewMode}
					portalTask={portalTask}
					portalTasksPriorities={portalTasksPriorities}
					portalTasksStatuses={portalTasksStatuses}
					projectId={projectId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>

			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>

			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingDelete}
					onSubmit={deleteItemsHandler}
					question={deleteConfirmText}
				>
					{deleteConfirmDescription}
				</Confirm>
			</Modal>

			<Table
				columns={columns}
				data={portalTasks}
				filters={filters}
				handleCustomDelete={handleCustomDelete}
				isCreatable={isProjectActive}
				isDeleteActionDisabled={!isProjectActive}
				isLoading={isLoading}
				isPinColumns
				isWithoutCheckbox={!isPortalAdmin}
				isWithoutDeleteAction={!isPortalAdmin}
				maxHeight='55vh'
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				stickyLeftColumnIndexes={[0, 1, 2, 3]}
				toggleAddModal={openTaskFormModal}
			/>
		</div>
	);
};

export default Tasks;
